import React, { useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react'
import MaskableSensitiveInput from '../MaskableSensitiveInput'
import {
  CLAIM_ADDITIONAL_FIELD_COMMON_MODE,
  CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE,
  additionalFieldGroupModeKey,
  additionalFieldInsurerValueKey,
  effectiveAdditionalFieldInputModeForGroup,
  effectiveAdditionalFieldInputType,
  isAdditionalFieldAlwaysPerInsurer,
  isAdditionalFieldVisible,
} from '../../lib/claimAdditionalFields'

function fieldInsurerIds(field) {
  return Array.isArray(field?.insurerIds) ? field.insurerIds.map(String).filter(Boolean) : []
}

function fieldInsurerNames(field) {
  return Array.isArray(field?.insurerNames) ? field.insurerNames : []
}

function fieldAppliesToInsurer(field, insurerId) {
  const target = String(insurerId ?? '')
  return target !== '' && fieldInsurerIds(field).includes(target)
}

function fieldValueKey(field) {
  return String(field?.valueKey || field?.fieldKey || '').trim()
}

function isFilledValue(field, raw) {
  if (field?.inputType === 'CHECKBOX') {
    return raw === 'Y' || raw === 'N'
  }
  return raw != null && String(raw).trim() !== ''
}

export default function ClaimAdditionalFieldInputs({
  fields,
  values,
  onChange,
  title = '추가 입력 항목',
  className = '',
  modeByGroupKey = {},
  onModeChange,
}) {
  const rows = useMemo(
    () => (Array.isArray(fields) ? fields.filter((field) => field?.fieldKey && field?.label) : []),
    [fields],
  )
  const [openByInsurerId, setOpenByInsurerId] = useState({})

  if (rows.length === 0) return null

  const valueMap = values && typeof values === 'object' ? values : {}
  const setValue = (key, value) => onChange({ ...valueMap, [key]: value })

  const insurerEntries = []
  const insurerSeen = new Set()
  rows.forEach((field) => {
    const ids = fieldInsurerIds(field)
    const names = fieldInsurerNames(field)
    ids.forEach((id, idx) => {
      if (insurerSeen.has(id)) return
      insurerSeen.add(id)
      insurerEntries.push({
        id,
        name: names[idx] || `보험사 ${insurerEntries.length + 1}`,
      })
    })
  })
  const hasInsurerScopedRows = insurerEntries.length > 0

  const modeForField = (field) => effectiveAdditionalFieldInputModeForGroup(field, rows, modeByGroupKey)

  const commonRows = rows.filter((field) => {
    const ids = fieldInsurerIds(field)
    if (!isAdditionalFieldVisible(field, rows, valueMap, '', modeByGroupKey)) return false
    if (ids.length === 0) return true
    if (ids.length <= 1) return false
    return modeForField(field) !== CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
  })

  const fieldsForInsurer = (insurerId) => rows.filter((field) => {
    const ids = fieldInsurerIds(field)
    if (!fieldAppliesToInsurer(field, insurerId)) return false
    if (!isAdditionalFieldVisible(field, rows, valueMap, insurerId, modeByGroupKey)) return false
    if (ids.length <= 1) return true
    return modeForField(field) === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
  })

  const valueKeyForField = (field, insurerId = '') => {
    const ids = fieldInsurerIds(field)
    if (insurerId && ids.length > 1 && modeForField(field) === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE) {
      return additionalFieldInsurerValueKey(field, insurerId)
    }
    return fieldValueKey(field)
  }

  const fieldIsMissing = (field, insurerId = '') => {
    if (!field?.required) return false
    return !isFilledValue(field, valueMap[valueKeyForField(field, insurerId)])
  }

  const summarizeFields = (targetFields, insurerId = '') => {
    const requiredFields = targetFields.filter((field) => field?.required)
    const missingFields = requiredFields.filter((field) => fieldIsMissing(field, insurerId))
    return {
      totalCount: targetFields.length,
      requiredCount: requiredFields.length,
      missingCount: missingFields.length,
      missingLabels: missingFields.map((field) => field.label).filter(Boolean),
    }
  }

  const renderStatusBadge = (summary) => {
    if (summary.missingCount > 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          필수 {summary.missingCount}개
        </span>
      )
    }
    if (summary.requiredCount > 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          완료
        </span>
      )
    }
    return (
      <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
        선택 {summary.totalCount}개
      </span>
    )
  }

  const renderLabel = (field) => (
    <div className="mb-1 flex min-w-0 flex-wrap items-center gap-1.5 text-sm text-slate-600">
      <span className="min-w-0 truncate">{field.label}</span>
      {field.required && <span className="text-rose-600">*</span>}
      {field.scopeLabel ? (
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
          {field.scopeLabel}
        </span>
      ) : null}
    </div>
  )

  const renderControl = (field, options = {}) => {
    const hideLabel = options.hideLabel === true
    const value = valueMap[field.fieldKey] ?? ''
    const inputType = effectiveAdditionalFieldInputType(field)
    if (inputType === 'TEXTAREA') {
      return (
        <div key={field.fieldKey} className="md:col-span-2">
          {!hideLabel ? renderLabel(field) : null}
          <textarea
            className="w-full rounded border px-3 py-2 text-sm"
            rows={3}
            value={value}
            autoComplete={field.sensitive ? 'off' : undefined}
            onChange={(e) => setValue(field.fieldKey, e.target.value)}
          />
        </div>
      )
    }
    if (inputType === 'SELECT') {
      return (
        <div key={field.fieldKey}>
          {!hideLabel ? renderLabel(field) : null}
          <select
            className="w-full rounded border px-3 py-2 text-sm"
            value={value}
            onChange={(e) => setValue(field.fieldKey, e.target.value)}
          >
            <option value="">선택</option>
            {(field.options || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label || option.value}
              </option>
            ))}
          </select>
        </div>
      )
    }
    if (inputType === 'RADIO') {
      return (
        <div key={field.fieldKey}>
          {!hideLabel ? renderLabel(field) : null}
          <div className="flex flex-wrap gap-2">
            {(field.options || []).map((option) => (
              <label key={option.value} className="inline-flex items-center gap-1.5 rounded border px-3 py-2 text-sm">
                <input
                  type="radio"
                  name={`additional_${field.fieldKey}`}
                  checked={value === option.value}
                  onChange={() => setValue(field.fieldKey, option.value)}
                />
                {option.label || option.value}
              </label>
            ))}
          </div>
        </div>
      )
    }
    if (inputType === 'CHECKBOX') {
      return (
        <div key={field.fieldKey} className="flex items-end">
          <label className="inline-flex w-full items-center gap-2 rounded border px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={value === 'Y'}
              onChange={(e) => setValue(field.fieldKey, e.target.checked ? 'Y' : 'N')}
            />
            <span className="min-w-0">
              {hideLabel ? '선택' : field.label}
              {!hideLabel && field.required && <span className="ml-1 text-rose-600">*</span>}
              {!hideLabel && field.scopeLabel ? (
                <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                  {field.scopeLabel}
                </span>
              ) : null}
            </span>
          </label>
        </div>
      )
    }
    if (inputType === 'DATE') {
      return (
        <div key={field.fieldKey}>
          {!hideLabel ? renderLabel(field) : null}
          <input
            type="date"
            className="w-full rounded border px-3 py-2 text-sm"
            value={value}
            autoComplete="off"
            onChange={(e) => setValue(field.fieldKey, e.target.value)}
          />
        </div>
      )
    }
    if (field.sensitive) {
      return (
        <div key={field.fieldKey}>
          {!hideLabel ? renderLabel(field) : null}
          <MaskableSensitiveInput
            value={value}
            onChange={(next) => setValue(field.fieldKey, next)}
            maskKind="text"
            className="w-full rounded border px-3 py-2 text-sm"
          />
        </div>
      )
    }
    return (
      <div key={field.fieldKey}>
        {!hideLabel ? renderLabel(field) : null}
        <input
          id={`claim-additional-field-${field.fieldKey}`}
          type={inputType === 'NUMBER' ? 'number' : 'text'}
          className="w-full rounded border px-3 py-2 text-sm"
          value={value}
          onChange={(e) => setValue(field.fieldKey, e.target.value)}
        />
      </div>
    )
  }

  const renderModeToggle = (field, mode) => {
    const insurerIds = fieldInsurerIds(field)
    const canChooseMode = insurerIds.length > 1 && typeof onModeChange === 'function'
    if (!canChooseMode) return null
    if (isAdditionalFieldAlwaysPerInsurer(field)) {
      return (
        <span className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
          보험사별 고정
        </span>
      )
    }

    const modeKey = additionalFieldGroupModeKey(field)
    const setMode = (nextMode) => {
      if (!modeKey) return
      onModeChange(modeKey, nextMode)
    }

    return (
      <div className="inline-flex overflow-hidden rounded border border-slate-200 text-xs">
        <button
          type="button"
          className={`px-2.5 py-1 ${mode === CLAIM_ADDITIONAL_FIELD_COMMON_MODE ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          onClick={() => setMode(CLAIM_ADDITIONAL_FIELD_COMMON_MODE)}
        >
          공통
        </button>
        <button
          type="button"
          className={`border-l border-slate-200 px-2.5 py-1 ${mode === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          onClick={() => setMode(CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE)}
        >
          보험사별
        </button>
      </div>
    )
  }

  const renderModeAwareControl = (field) => {
    const insurerIds = fieldInsurerIds(field)
    const mode = modeForField(field)
    if (insurerIds.length <= 1 || typeof onModeChange !== 'function') return renderControl(field)

    const modeKey = additionalFieldGroupModeKey(field)
    return (
      <div key={modeKey || field.fieldKey} className="md:col-span-2 rounded border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-medium text-slate-800">
              <span className="min-w-0 truncate">{field.label}</span>
              {field.required && <span className="text-rose-600">*</span>}
              {field.scopeLabel ? (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                  {field.scopeLabel}
                </span>
              ) : null}
            </div>
          </div>
          {renderModeToggle(field, mode)}
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {renderControl(field, { hideLabel: true })}
        </div>
      </div>
    )
  }

  const renderInsurerScopedControl = (field, insurer) => {
    const ids = fieldInsurerIds(field)
    const mode = modeForField(field)
    const scopedField = {
      ...field,
      fieldKey: valueKeyForField(field, insurer.id),
      scopeLabel: ids.length > 1 ? insurer.name : (field.scopeLabel || insurer.name),
    }
    if (ids.length <= 1 || typeof onModeChange !== 'function') {
      return renderControl(scopedField)
    }

    const key = `${fieldValueKey(field)}__${insurer.id}`
    return (
      <div key={key} className="md:col-span-2 rounded border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-medium text-slate-800">
            <span className="min-w-0 truncate">{field.label}</span>
            {field.required && <span className="text-rose-600">*</span>}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
              {insurer.name}
            </span>
          </div>
          {renderModeToggle(field, mode)}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {renderControl(scopedField, { hideLabel: true })}
        </div>
      </div>
    )
  }

  const renderSimpleLayout = () => (
    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
      {rows.map((field) => renderControl(field))}
    </div>
  )

  const renderCommonSection = () => {
    if (commonRows.length === 0) return null
    const summary = summarizeFields(commonRows)
    return (
      <section className="rounded border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-medium text-slate-900">공통 추가입력</div>
          {renderStatusBadge(summary)}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {commonRows.map((field) => renderModeAwareControl(field))}
        </div>
      </section>
    )
  }

  const renderInsurerCard = (insurer) => {
    const insurerFields = fieldsForInsurer(insurer.id)
    if (insurerFields.length === 0) return null

    const summary = summarizeFields(insurerFields, insurer.id)
    const open = openByInsurerId[insurer.id] ?? true
    const toggleOpen = () => setOpenByInsurerId((prev) => ({ ...prev, [insurer.id]: !open }))

    return (
      <section key={insurer.id} className="rounded border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={toggleOpen}
          >
            {open ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            )}
            <span className="min-w-0 truncate font-medium text-slate-900">{insurer.name}</span>
            <span className="shrink-0 text-xs text-slate-500">{summary.totalCount}개</span>
          </button>
          <div className="flex shrink-0 items-center gap-2">
            {renderStatusBadge(summary)}
          </div>
        </div>
        {open ? (
          <div className="border-t border-slate-200 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {insurerFields.map((field) => renderInsurerScopedControl(field, insurer))}
            </div>
          </div>
        ) : null}
      </section>
    )
  }

  return (
    <div className={`rounded border p-3 ${className}`.trim()}>
      {title ? <div className="text-sm font-medium text-slate-800">{title}</div> : null}

      {hasInsurerScopedRows ? (
        <div className="mt-3 space-y-3">
          {renderCommonSection()}
          {insurerEntries.map((insurer) => renderInsurerCard(insurer))}
        </div>
      ) : (
        renderSimpleLayout()
      )}
    </div>
  )
}
